import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import { styled, Theme } from "@mui/material/styles";
import Toolbar from "@mui/material/Toolbar";
import * as React from "react";

import { StickyFooterShell } from "../Footer/StickyFooterShell";
import Header from "../Header";

/**
 * The signed-in chrome: the fixed app bar with the nav in it, and the scrolling
 * main column under it.
 *
 * It used to be two layouts in one file, chosen by config.v2.nav: this shell,
 * and a 240px MUI permanent drawer with its own hamburger, its own link list
 * and a non-sticky footer. Every tenant is on the nav in the app bar now, so
 * the drawer, the mixins that animated it and the icons it listed went with the
 * flag. The `open` prop stays on the AppBar because the styled component still
 * declares it; nothing sets it any more.
 */

const drawerWidth = 240;

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }: { theme: Theme; open?: boolean }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

export default function MainContainer({
  children,
  v2Footer = false,
}: {
  children: React.ReactElement;
  v2Footer?: boolean;
}) {
  return (
    <Box>
      <CssBaseline />
      <AppBar
        sx={{
          // Match the wrapping AppBar to the nav (and drop the elevation
          // shadow) so its background and shadow don't peek around the shorter
          // nav as a stray hairline.
          backgroundColor: "#edf2f7",
          boxShadow: "none",
        }}
        position="fixed"
      >
        <Toolbar disableGutters sx={{ px: "0px !important" }}>
          <Box sx={{ width: 1 }}>
            <Header />
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          ml: 0,
          position: "fixed",
          top: "4rem",
          left: 0,
          right: 0,
          bottom: 0,
          overflowY: "auto",
        }}
      >
        <StickyFooterShell v2Footer={v2Footer}>{children}</StickyFooterShell>
      </Box>
    </Box>
  );
}
